'use client';

import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';
import Link from 'next/link';

const floatingCards = [
{
  id: 'pickup',
  className: 'float-a absolute top-[12%] left-[2%] md:left-[8%] z-20',
  content:
  <div className="glass-card px-5 py-4 flex items-center gap-3 min-w-[200px] shadow-float">
        <div className="w-10 h-10 rounded-full bg-success/20 flex items-center justify-center flex-shrink-0">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2.5" strokeLinecap="round">
            <path d="M20 6L9 17l-5-5" />
          </svg>
        </div>
        <div>
          <p className="text-white text-xs font-700 leading-tight">Pickup Scheduled!</p>
          <p className="text-white/60 text-[11px] font-500 mt-0.5">Today · Block A · 4:00 PM</p>
        </div>
      </div>

},
{
  id: 'ready',
  className: 'float-c absolute top-[32%] right-[2%] md:right-[6%] z-20',
  content:
  <div className="glass-card px-5 py-4 flex items-center gap-3 min-w-[190px] shadow-float">
        <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#38BDF8" strokeWidth="2.5" strokeLinecap="round">
            <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
          </svg>
        </div>
        <div>
          <p className="text-white text-xs font-700 leading-tight">Clothes Ready! 🎉</p>
          <p className="text-white/60 text-[11px] font-500 mt-0.5">Fresh & folded · Delivered</p>
        </div>
      </div>

},
{
  id: 'time',
  className: 'float-b absolute bottom-[28%] left-[3%] md:left-[10%] z-20',
  content:
  <div className="glass-card px-5 py-4 flex items-center gap-3 min-w-[170px] shadow-float">
        <div className="w-10 h-10 rounded-full bg-warning/20 flex items-center justify-center flex-shrink-0">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2.5" strokeLinecap="round">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
        </div>
        <div>
          <p className="text-white text-xs font-700 leading-tight">3 hrs saved</p>
          <p className="text-white/60 text-[11px] font-500 mt-0.5">This week alone</p>
        </div>
      </div>

},
{
  id: 'rating',
  className: 'float-d absolute bottom-[14%] right-[4%] md:right-[10%] z-20',
  content:
  <div className="glass-card px-5 py-4 flex items-center gap-3 min-w-[160px] shadow-float">
        <div className="flex -space-x-2">
          <AppImage
        src="https://img.rocket.new/generatedImages/rocket_gen_img_1801bd296-1763301681318.png"
        alt="Student avatar 1"
        width={28} height={28}
        className="w-7 h-7 rounded-full border-2 border-white/30 object-cover" />
      
          <AppImage
        src="https://img.rocket.new/generatedImages/rocket_gen_img_1801bd296-1763301681318.png"
        alt="Student avatar 2"
        width={28} height={28}
        className="w-7 h-7 rounded-full border-2 border-white/30 object-cover" />
      
          <AppImage
        src="https://img.rocket.new/generatedImages/rocket_gen_img_1801bd296-1763301681318.png"
        alt="Student avatar 3"
        width={28} height={28}
        className="w-7 h-7 rounded-full border-2 border-white/30 object-cover" />
      
        </div>
        <div>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((i) =>
        <svg key={i} width="10" height="10" viewBox="0 0 24 24" fill="#F59E0B">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
        )}
          </div>
          <p className="text-white/60 text-[11px] font-500 mt-0.5">4.9 · 1,200+ reviews</p>
        </div>
      </div>

}];


export default function HeroSection() {
  const heroRef = useRef<HTMLDivElement>(null);

  // Cursor parallax effect on floating cards
  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;

    const handleMouseMove = (e: MouseEvent) => {
      const { clientX, clientY } = e;
      const { width, height, left, top } = hero.getBoundingClientRect();
      const xRatio = (clientX - left) / width - 0.5;
      const yRatio = (clientY - top) / height - 0.5;

      const cards = hero.querySelectorAll<HTMLElement>('[data-parallax]');
      cards.forEach((card, i) => {
        const depth = (i % 3 + 1) * 8;
        card.style.transform = `translate(${xRatio * depth}px, ${yRatio * depth}px)`;
      });
    };

    hero.addEventListener('mousemove', handleMouseMove);
    return () => hero.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen hero-gradient noise-overlay overflow-hidden flex items-center">
      
      {/* Background blobs */}
      <div
        className="blob absolute top-[-10%] left-[-5%] w-[500px] h-[500px] opacity-20"
        style={{ background: 'radial-gradient(circle, #38BDF8 0%, transparent 70%)' }} />
      
      <div
        className="blob absolute bottom-[-10%] right-[-5%] w-[600px] h-[600px] opacity-15"
        style={{
          background: 'radial-gradient(circle, #1A56DB 0%, transparent 70%)',
          animationDelay: '-4s'
        }} />
      

      {/* Floating Cards */}
      {floatingCards.map((card) =>
      <div key={card.id} className={card.className} data-parallax>
          {card.content}
        </div>
      )}

      {/* Main content */}
      <div className="relative z-10 max-w-[1280px] mx-auto px-6 py-32 text-center">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-2 mb-8">
          <span className="w-2 h-2 rounded-full bg-success animate-pulse" />
          <span className="text-white/90 text-xs font-700 uppercase tracking-wider">
            Official Laundry Service — Managed by College Administration
          </span>
        </div>

        {/* Headline */}
        <h1 className="font-display font-800 tracking-tight leading-[0.9] mb-8 text-white">
          <span className="block text-[clamp(56px,10vw,120px)]">Laundry Day</span>
          <span className="block text-[clamp(56px,10vw,120px)] gradient-text">Is Cancelled.</span>
        </h1>

        {/* Sub */}
        <p className="text-white/70 text-[clamp(16px,2vw,22px)] font-400 max-w-2xl mx-auto leading-relaxed mb-12">
          Schedule a pickup from your hostel room, we wash &amp; fold everything,
          then deliver it back to your door. <strong className="text-white font-700">No hassle. No waiting.</strong>
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-20">
          <Link href="/dashboard" className="bg-white text-primary px-10 py-5 rounded-full font-800 text-lg hover:scale-105 transition-transform shadow-hero cursor-pointer">
            Schedule Pickup
          </Link>
          <Link href="/login" className="flex items-center gap-3 text-white/80 hover:text-white transition-colors group cursor-pointer">
            <div className="w-12 h-12 rounded-full bg-white/10 border border-white/20 flex items-center justify-center group-hover:bg-white/20 transition-colors">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                <polygon points="5 3 19 12 5 21 5 3" />
              </svg>
            </div>
            <span className="text-sm font-600">Sign In to Your Account</span>
          </Link>
        </div>

        {/* Tile grid stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
          {[
          { value: '1,200+', label: 'Loads Completed', rotate: 'rotate-1' },
          { value: '₹1,200', label: 'Starting Price', rotate: '-rotate-1' },
          { value: '24hr', label: 'Turnaround Time', rotate: 'rotate-2' },
          { value: '4.9★', label: 'Student Rating', rotate: '-rotate-2' }].
          map((stat) =>
          <div
            key={stat.value}
            className={`glass-card p-5 text-center ${stat.rotate} hover:rotate-0 transition-transform duration-300`}>
            
              <p className="text-white text-2xl font-800 tracking-tight">{stat.value}</p>
              <p className="text-white/50 text-[11px] font-600 uppercase tracking-wider mt-1">{stat.label}</p>
            </div>
          )}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 opacity-50">
        <span className="text-white text-[11px] font-600 uppercase tracking-widest">Scroll</span>
        <div className="w-[1px] h-8 bg-white/40 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1/2 bg-white animate-bounce" />
        </div>
      </div>
    </section>);

}